/**
 * Converts people with embedded relationships into the daveagp/family-tree text format.
 * - Spouses => "Name1 + Name2"
 * - Children => c: child1, child2
 *
 * IMPORTANT: Sub-lines (c:, l:, p:, n:) MUST start with a leading space
 * for the tree parser to recognize them as sub-entries.
 */
export function buildFamilyTreeText(people) {
  if (!people.length) return ''

  const peopleMap = {}
  people.forEach((p) => {
    peopleMap[p.id] = p
  })

  function getName(id) {
    return (peopleMap[id] && peopleMap[id].name) || '?'
  }

  let entries = []
  const processedCouples = new Set()

  // Process all marriages (family units)
  people.forEach((person) => {
    const spouses = person.spouses || []
    
    spouses.forEach((spouseId) => {
      // Create unique key for couple (sorted to avoid duplicates)
      const coupleKey = [person.id, spouseId].sort().join('__')
      
      // Skip if we've already processed this couple
      if (processedCouples.has(coupleKey)) return
      processedCouples.add(coupleKey)
      
      const spouse = peopleMap[spouseId]
      if (!spouse) return
      
      // Find shared children
      const personChildren = new Set(person.children || [])
      const spouseChildren = spouse.children || []
      const sharedChildren = spouseChildren.filter(childId => personChildren.has(childId))
      
      let subLines = []
      
      // Add children
      if (sharedChildren.length > 0) {
        const childNames = sharedChildren.map(getName)
        subLines.push(` c: ${childNames.join(', ')}`)
      }
      
      // Build the marriage block
      let block = `${person.name} + ${spouse.name}`
      if (subLines.length > 0) {
        block += '\n' + subLines.join('\n')
      }
      entries.push(block)
    })
  })

  // Add individual person details (birthYear/deathYear converted to lifespan, photo, notes)
  people.forEach((person) => {
    const lines = []
    
    // Convert birthYear/deathYear to lifespan format for tree display
    if (person.birthYear || person.deathYear) {
      const birth = person.birthYear || '?'
      const death = person.deathYear || ''
      const lifespan = death ? `${birth}-${death}` : birth
      lines.push(` l: ${lifespan}`)
    }
    
    if (person.photo) {
      lines.push(` p: ${person.photo}`)
    }
    if (person.notes && person.notes.length > 0) {
      person.notes.forEach((note) => lines.push(` n: ${note}`))
    }

    if (lines.length > 0) {
      const block = `${person.name}\n` + lines.join('\n')
      entries.push(block)
    }
  })

  return entries.join('\n\n')
}
